// src/seo/blog/leadMagnetArticles.ts
//
// BLOG-LEAD-MAGNET-SAMPLES-001
//
// ONE record per lead-magnet article, and the ONLY place each article's title,
// meta description, hero image, FAQ copy and free-sample classification live.
// Same design as src/seo/blog/esaHousingGuides.ts: the visible FAQ, the
// build-time <head> JSON-LD (getRouteHeadJsonLd in src/prerender/entry.tsx),
// the client <BlogArticleSchema>, CORE_PAGE_META (src/config/seoConfig.ts), the
// og:image pass (scripts/prerender-seo.mjs), the /blog cards and the sample
// offer registry all read these records. scripts/check-blog-lead-magnet.mjs
// asserts they agree.
//
// Imported through jiti by seoConfig / prerender-seo WITHOUT the "@" alias, so
// this module must only use relative imports (it has none).
//
// SOURCE CONTENT: the owner's three DOCX drafts of 2026-09-28 —
//   "What Happens After You Submit an ESA Letter for Housing"
//   "Blog Post: Free Pet Care Planner"
//   "Blog Post: Free PSD Training Workbook"
// adapted to PawTenant. Deliberate changes from the drafts:
//   * The drafts promised a free 34-page planner / 30-page workbook. The offer
//     is a FREE 15-PAGE SAMPLE; the complete editions are included with
//     eligible PawTenant orders. Every title, meta, FAQ and CTA says so.
//   * No author, reviewer or credential is claimed (the drafts carried
//     placeholders). Author and publisher are the PawTenant organisation.
//   * Legal statements were checked against primary sources on 2026-10-01;
//     unverifiable ones were removed or qualified (see each page file).
//   * The form asks only for an email address — never a name.

export interface LeadMagnetFaq {
  q: string;
  a: string;
}

export type LeadMagnetSampleOffer = "esa" | "psd" | "choice";

export interface LeadMagnetArticle {
  route: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  breadcrumb: string;
  heroImage: string;
  heroAlt: string;
  heroWidth: number;
  heroHeight: number;
  datePublished: string;
  dateModified: string;
  articleSection: string;
  keywords: string[];
  /** Minutes, shown on the /blog card. */
  readTime: number;
  /** Explicit, reviewed free-sample classification (see blogSampleOfferRegistry.ts). */
  sampleOffer: LeadMagnetSampleOffer;
  faqs: LeadMagnetFaq[];
}

export const BASE_URL = "https://pawtenant.com";

/** Middle crumb of every article's breadcrumb. */
export const LEAD_MAGNET_PARENT = { to: "/blog", label: "Blog" } as const;

const HERO_W = 1200;
const HERO_H = 630;
const PUBLISHED = "2026-10-01";

export const WHAT_HAPPENS_AFTER_ROUTE = "/what-happens-after-you-submit-esa-letter";
export const FREE_PLANNER_ROUTE = "/free-pet-care-planner-esa-psychiatric-service-dog";
export const FREE_WORKBOOK_ROUTE = "/free-psychiatric-service-dog-training-workbook";

export const LEAD_MAGNET_ARTICLES: LeadMagnetArticle[] = [
  {
    route: WHAT_HAPPENS_AFTER_ROUTE,
    h1: "What Happens After You Submit an ESA Letter for Housing? (2026 Guide)",
    metaTitle: "What Happens After You Submit an ESA Letter? (2026 Guide)",
    metaDescription:
      "Submitted an ESA letter for housing? See how landlords review it, what HUD's May 2026 change means, and how state ESA laws affect you.",
    breadcrumb: "What Happens After You Submit an ESA Letter",
    heroImage: "/assets/blog/lead-magnets/what-happens-after-you-submit-esa-letter-1200x630.webp",
    heroAlt: "Smiling woman crouching beside a large white dog outside an apartment building",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    articleSection: "Housing Rights",
    keywords: [
      "what happens after you submit an ESA letter",
      "emotional support animal letter for housing",
      "ESA housing letter",
      "landlord emotional support animal letter",
      "ESA laws by state",
    ],
    readTime: 11,
    sampleOffer: "esa",
    faqs: [
      {
        q: "How long does a landlord have to respond to an ESA letter?",
        a: "No federal law sets a fixed number of days. The 2004 HUD and Department of Justice Joint Statement on reasonable accommodations says an undue delay may be treated as a failure to accommodate, and Iowa law requires a written response within a reasonable time. If about two weeks pass with no answer, follow up in writing.",
      },
      {
        q: "Can a landlord contact my provider to verify my ESA letter?",
        a: "A landlord may confirm that the letter is genuine and that the provider is licensed, for example by checking the state licensing board or asking whether the provider wrote it. A landlord should not ask for your diagnosis, therapy notes or treatment history, and your provider cannot share your health information without your written authorization.",
      },
      {
        q: "Can I submit an ESA letter after I have already moved in?",
        a: "Yes. You can ask for a reasonable accommodation at any point in a tenancy. Asking before the animal moves in is usually best, because an animal that arrives before approval can lead to a lease-violation notice while the request is reviewed.",
      },
      {
        q: "Can a landlord deny my ESA because of its breed or size?",
        a: "A denial for safety reasons should rest on an individualized assessment of the specific animal's actual conduct, not on breed or size stereotypes. Since May 2026, HUD's own enforcement focuses on individually trained animals, so for an untrained emotional support animal your state and local law and your circumstances matter more.",
      },
      {
        q: "Does an ESA letter expire?",
        a: "Federal fair housing law sets no expiration date for an ESA letter. Many housing providers ask for documentation dated within the last year, and some states set their own rules: Arkansas requires a yearly evaluation for emotional support dogs, and in Iowa a provider's written finding is valid for 12 months or the lease term, whichever is longer.",
      },
      {
        q: "Is my ESA letter still valid after HUD's 2026 memo?",
        a: "The May 2026 memorandum changed how HUD itself enforces the Fair Housing Act; it did not cancel ESA letters or amend the statute. A letter from a licensed provider who evaluated you can still support a request under state and local law and in a private lawsuit.",
      },
    ],
  },
  {
    route: FREE_PLANNER_ROUTE,
    h1: "Free Pet Care Planner Sample for ESA and Psychiatric Service Dog Owners (15-Page PDF)",
    metaTitle: "Free Pet Care Planner Sample PDF for ESA & PSD Owners",
    metaDescription:
      "Get a free 15-page printable sample of the PawTenant Pet Care Planner to track vaccines, medications, vet details and training for your ESA or service dog.",
    breadcrumb: "Free Pet Care Planner Sample",
    heroImage: "/assets/blog/lead-magnets/free-pet-care-planner-sample-1200x630.webp",
    heroAlt: "Three pages of the free 15-page PawTenant Pet Care Planner sample: cover, vaccination tracker and emergency information",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    articleSection: "Free Resources",
    keywords: [
      "free pet care planner",
      "printable pet care planner",
      "ESA owner checklist",
      "emotional support animal records",
      "psychiatric service dog training log",
    ],
    readTime: 8,
    // Written for both ESA owners and PSD handlers: the visitor chooses which
    // free sample they want (the ESA planner sample or the PSD workbook sample).
    sampleOffer: "choice",
    faqs: [
      {
        q: "Is the Pet Care Planner sample really free?",
        a: "Yes. Enter your email address and we will send you a free 15-page sample of the planner as a printable PDF, with no payment and no account. The complete 33-page planner is included with eligible PawTenant ESA letter orders.",
      },
      {
        q: "Do I need an ESA or a service dog to use it?",
        a: "No. The planner works for any pet owner. It was designed with emotional support animal owners and psychiatric service dog handlers in mind, because they often need organized records.",
      },
      {
        q: "Is the planner an ESA letter or a PSD letter?",
        a: "No. It is an organizing tool. An ESA letter comes from a licensed provider who has evaluated you, and a planner cannot replace that evaluation.",
      },
      {
        q: "Is a training log required for a psychiatric service dog?",
        a: "No. Under the ADA a service dog must be individually trained to do work or perform tasks related to your disability, but the ADA does not require a training log or any certification. A log is simply a helpful record.",
      },
      {
        q: "Can I use the planner for cats and other animals?",
        a: "Yes. Most pages apply to any companion animal. Skip the dog-specific items, such as walks, if they do not apply.",
      },
      {
        q: "Will you add me to a mailing list?",
        a: "Only if you tick the separate, optional box to receive PawTenant tips. Requesting the sample on its own sends you one email with the PDF attached.",
      },
    ],
  },
  {
    route: FREE_WORKBOOK_ROUTE,
    h1: "Free Psychiatric Service Dog Training Workbook Sample for Owner-Trainers (15-Page PDF)",
    metaTitle: "Free Psychiatric Service Dog Training Workbook Sample (PDF)",
    metaDescription:
      "Get a free 15-page sample of our psychiatric service dog training workbook. Log tasks and public access, and learn what the ADA does and does not require.",
    breadcrumb: "Free PSD Training Workbook Sample",
    heroImage: "/assets/blog/lead-magnets/free-psd-training-workbook-sample-1200x630.webp",
    heroAlt: "Three pages of the free 15-page PawTenant PSD Training Workbook sample: cover, task training framework and 120-hour tracker",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    articleSection: "Free Resources",
    keywords: [
      "psychiatric service dog training workbook",
      "psychiatric service dog training log",
      "owner-trained service dog",
      "service dog public access",
      "PSD letter",
    ],
    readTime: 9,
    sampleOffer: "psd",
    faqs: [
      {
        q: "Is the PSD Training Workbook sample really free?",
        a: "Yes. Enter your email address and we will send you a free 15-page sample of the workbook as a printable PDF, with no payment and no account. The complete 30-page workbook is included with eligible PawTenant PSD letter orders.",
      },
      {
        q: "Can I train my own psychiatric service dog?",
        a: "Yes. The ADA does not require professional training, so you may train your dog yourself. Many handlers still work with a qualified trainer and their veterinarian, especially for foundation skills and public access.",
      },
      {
        q: "Do I need a vest, certification or registration?",
        a: "No. The ADA does not require a vest, ID card, certification or registration. What matters is that your dog is individually trained to do work or perform tasks related to your disability and is under control in public.",
      },
      {
        q: "Is a PSD letter required?",
        a: "Not for ADA public access, and not for flying under the U.S. Department of Transportation's rules. A landlord may ask for reliable information about your disability-related need when it is not obvious, and a letter from a licensed provider can support that. A letter does not certify your dog's training.",
      },
      {
        q: "Can a service dog in training go anywhere a service dog can?",
        a: "Not under federal law. The ADA covers only dogs that are already trained. Some states give dogs in training access rights, usually with conditions, so check the law where you train.",
      },
      {
        q: "Does the workbook replace a professional trainer?",
        a: "No. It is a planning and recordkeeping tool. If your dog shows fear, aggression or pain, talk to a veterinarian or a qualified behavior professional.",
      },
    ],
  },
];

export const LEAD_MAGNET_ROUTES: string[] = LEAD_MAGNET_ARTICLES.map((a) => a.route);

export function leadMagnetArticle(route: string): LeadMagnetArticle | undefined {
  return LEAD_MAGNET_ARTICLES.find((a) => a.route === route);
}

/**
 * The full @graph for one article: Article + BreadcrumbList + FAQPage, built
 * from the record so the schema cannot describe copy the page does not render.
 * No Offer / Product / Review / rating markup — the sample is not a product
 * listing. Returns null for any route that is not a lead-magnet article.
 */
export function leadMagnetArticleGraph(route: string): Record<string, unknown> | null {
  const a = leadMagnetArticle(route);
  if (!a) return null;
  const canonical = `${BASE_URL}${a.route}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${canonical}#article`,
        headline: a.h1,
        description: a.metaDescription,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        url: canonical,
        image: [`${BASE_URL}${a.heroImage}`],
        datePublished: a.datePublished,
        dateModified: a.dateModified,
        author: { "@type": "Organization", name: "PawTenant", url: `${BASE_URL}/` },
        publisher: { "@type": "Organization", name: "PawTenant", url: `${BASE_URL}/` },
        articleSection: a.articleSection,
        keywords: a.keywords.join(", "),
        inLanguage: "en-US",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
          { "@type": "ListItem", position: 2, name: LEAD_MAGNET_PARENT.label, item: `${BASE_URL}${LEAD_MAGNET_PARENT.to}` },
          { "@type": "ListItem", position: 3, name: a.breadcrumb, item: canonical },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: a.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}
